import { AxiosResponse } from 'axios';

import { IGetParams, IListResponse, IPaginatedListResponse } from '@/api/types';
import { ApiVersion } from '@/config';
import {
  IAgency,
  IAssignedUnitShiftStatusReq,
  ICFSCloseReason,
  ICFSLog,
  ICFSRelatedRecordMutation,
  ICFSRelatedRecordsResponse,
  ICFSRelatedRecordSearchResponse,
  IFile,
  INarrative,
  INarrativeRequest,
} from '@/models';
import {
  AssignmentStatus,
  IAdditionalInfo,
  ICFS,
  ICFSForm,
  ICFSMasterName,
  ICFSMasterVehicle,
  ICFSInvolvement,
  IRequiredUnitForm,
  TSeverity,
  ICallerLocation,
  IAddressInfo,
} from '@/models/cfs';
import { INote } from '@/models/note';
import { ICFSTimelineLog } from '@/pages/CFS/ActiveCFSList/components/CfsTimelinePanel';

import { http } from './http';

export type TActiveCFSFilter = {
  severity?: TSeverity;
  assignmentStatus?: AssignmentStatus;
  agencies?: string[];
  readBy?: boolean;
  isReadyToDispatch?: boolean;
  agencyType?: string[];
};

export type ICFSFromIncomingCallReq = {
  disposition?: string;
  incomingCallNotes?: string;
  incomingCallId?: string;
  address?: string;
  businessName?: string;
  primaryReporter?: string;
  reporter: {
    callerPhoneNumber: string;
  };
};

export type ICFSFromCarStopReq = {
  locationPoint: {
    type: 'Point';
    coordinates: [number, number];
  };
};

export const CfsApi = {
  create: async (): Promise<AxiosResponse<ICFS>> => {
    return http.post(`api/${ApiVersion.V1}/cfs`);
  },
  createFromIncomingCall: async (
    data: ICFSFromIncomingCallReq,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/incoming-call`, data);
  },
  createFromCarStop: async (
    data: ICFSFromCarStopReq,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/car-stop`, data);
  },
  getOne: async (id: string): Promise<AxiosResponse<ICFS>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${id}`);
  },
  getRelatedRecords: async (
    cfsId: string,
  ): Promise<AxiosResponse<ICFSRelatedRecordsResponse>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${cfsId}/related-records`);
  },
  searchRelatedRecords: async (
    cfsId: string,
    params: {
      type: 'CASE' | 'CFS' | 'TASK' | 'WARRANT';
      q?: string;
      page?: number;
      limit?: number;
    },
  ): Promise<AxiosResponse<ICFSRelatedRecordSearchResponse>> => {
    return http.get(
      `api/${ApiVersion.V1}/cfs/${cfsId}/related-records/search`,
      {
        params,
      },
    );
  },
  linkRelatedRecord: async (
    cfsId: string,
    data: ICFSRelatedRecordMutation,
  ): Promise<AxiosResponse<ICFSRelatedRecordsResponse>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/${cfsId}/related-records`, data);
  },
  unlinkRelatedRecord: async (
    cfsId: string,
    data: ICFSRelatedRecordMutation,
  ): Promise<AxiosResponse<ICFSRelatedRecordsResponse>> => {
    return http.delete(`api/${ApiVersion.V1}/cfs/${cfsId}/related-records`, {
      data,
    });
  },
  list: async (
    params: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<ICFS>>> => {
    return http.get(`api/${ApiVersion.V1}/cfs`, { params });
  },
  listIds: async (
    params: IGetParams,
  ): Promise<AxiosResponse<{ ids: string[] }>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/ids`, { params });
  },
  activeList: async (params?: IGetParams): Promise<AxiosResponse<ICFS[]>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/active`, {
      params: {
        limit: 1000,
        ...params,
      },
    });
  },
  /**
   * Fetches past CFS assignments for the current user.
   * Returns CFS records where the user was assigned and the assignment is completed.
   */
  getPastAssignments: async (
    searchQuery?: string,
  ): Promise<AxiosResponse<{ docs: ICFS[] }>> => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    oneMonthAgo.setHours(0, 0, 0, 0);

    const filterModel: {
      items: { field: string; operator: string; value: string }[];
      logicOperator: string;
      quickFilterValues?: string[];
    } = {
      items: [
        {
          field: 'createdAt',
          operator: 'onOrAfter',
          value: oneMonthAgo.toISOString(),
        },
      ],
      logicOperator: 'and',
    };

    if (searchQuery?.trim()) {
      filterModel.quickFilterValues = [searchQuery.trim()];
    }

    return http.get(`api/${ApiVersion.V1}/cfs/past-assignments`, {
      params: {
        limit: 1000,
        page: 1,
        filter: JSON.stringify(filterModel),
      },
    });
  },
  update: async (
    id: string,
    data: Partial<ICFSForm>,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.put(`api/${ApiVersion.V1}/cfs/${id}`, data);
  },
  delete: async (id: string): Promise<AxiosResponse> => {
    return http.delete(`api/${ApiVersion.V1}/cfs/${id}`);
  },
  updateAddressInfo: async (
    cfsId: string,
    addressId?: string | null,
    data?: IAddressInfo,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.put(`api/${ApiVersion.V1}/cfs/${cfsId}/address-info`, {
      ...data,
      address: addressId,
    });
  },
  addAdditionalInfo: async (
    cfsId: string,
    data: Partial<IAdditionalInfo>,
  ): Promise<AxiosResponse> => {
    return http.post(`api/${ApiVersion.V1}/cfs/${cfsId}/additional-info`, data);
  },
  assignUnitShift: async (
    cfsId: string,
    unitShift: string,
    isDispatchedToHQ?: boolean,
    headquarterName?: string | null,
  ): Promise<AxiosResponse<ICFS>> => {
    const data = {
      unitShift,
      isDispatchedToHQ,
      ...(headquarterName === undefined || headquarterName === null
        ? {}
        : { headquarterName }),
    };

    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/assigned-unit-shifts`,
      data,
    );
  },
  addUnitShiftToQueue: async (
    cfsId: string,
    unitShiftId: string,
  ): Promise<AxiosResponse> => {
    return http.post(`api/${ApiVersion.V1}/cfs/${cfsId}/queue/${unitShiftId}`);
  },
  checkAgenciesUpdate: async (
    cfsId: string,
    {
      incidentCodes,
      address,
    }: {
      incidentCodes?: string[];
      address?: string | null;
    },
  ): Promise<AxiosResponse<IAgency[]>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/${cfsId}/agency/check`, {
      incidentCodes,
      address,
    });
  },
  updateIncidentCodes: async (
    cfsId: string,
    incidentCodes: string[],
  ): Promise<AxiosResponse<ICFS>> => {
    return http.patch(`api/${ApiVersion.V1}/cfs/${cfsId}/incident-codes`, {
      incidentCodes,
    });
  },
  updateAgencies: async (
    cfsId: string,
    agencies: string[],
  ): Promise<AxiosResponse<ICFS>> => {
    return http.patch(`api/${ApiVersion.V1}/cfs/${cfsId}/agency`, {
      agencies,
    });
  },
  updateRequiredUnits: async (
    cfsId: string,
    requiredUnits: IRequiredUnitForm[],
  ): Promise<AxiosResponse<ICFS>> => {
    return http.put(`api/${ApiVersion.V1}/cfs/${cfsId}/required-units`, {
      requiredUnits,
    });
  },
  updateAssignedUnitShiftStatus: async (
    cfsId: string,
    assignedUnitShiftId: string,
    data: IAssignedUnitShiftStatusReq,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.patch(
      `api/${ApiVersion.V1}/cfs/${cfsId}/assigned-unit-shifts/${assignedUnitShiftId}/status`,
      data,
    );
  },
  updateAssignedUnitShiftDispatchedToHQStatus: async (
    cfsId: string,
    unitShiftId: string,
    headquarterName?: string | null,
  ): Promise<AxiosResponse<ICFS>> => {
    const data =
      headquarterName === undefined || headquarterName === null
        ? {}
        : { headquarterName };

    return http.patch(
      `api/${ApiVersion.V1}/cfs/${cfsId}/assigned-unit-shifts/${unitShiftId}/dispatch-to-hq`,
      data,
    );
  },
  getCloseReasons: async (): Promise<AxiosResponse<ICFSCloseReason[]>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/close-reasons`);
  },
  updateCloseReason: async (
    id: string,
    closeReason: string,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.patch(`api/${ApiVersion.V1}/cfs/${id}/close-reason`, {
      closeReason,
    });
  },
  closeCFS: async (
    id: string,
    closeReason: string,
    assignedOfficer?: string,
    agencyPrimaryOfficers?: { agency: string; primaryOfficer: string | null }[],
  ): Promise<AxiosResponse> => {
    return http.put(`api/${ApiVersion.V1}/cfs/${id}/close`, {
      closeReason,
      assignedOfficer,
      agencyPrimaryOfficers,
    });
  },
  updatePrimaryOfficer: async (
    id: string,
    primaryOfficer?: string,
  ): Promise<AxiosResponse> => {
    return http.put(`api/${ApiVersion.V1}/cfs/${id}/primary-officer`, {
      primaryOfficer,
    });
  },
  reopenCFS: async (id: string): Promise<AxiosResponse> => {
    return http.put(`api/${ApiVersion.V1}/cfs/${id}/reopen`);
  },
  addCfsMasterVehicle: async (
    cfsId: string,
    vehicleId: string,
  ): Promise<AxiosResponse<ICFSMasterVehicle>> => {
    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/master-vehicles/${vehicleId}`,
    );
  },
  getCfsMasterVehicles: async (
    cfsId: string,
  ): Promise<AxiosResponse<ICFSMasterVehicle[]>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${cfsId}/master-vehicles`);
  },
  getPaginatedCfsMasterVehicles: async (
    cfsId: string,
  ): Promise<AxiosResponse<IPaginatedListResponse<ICFSMasterVehicle>>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${cfsId}/master-vehicles/list`);
  },
  getCfsMasterVehicle: async (
    cfsId: string,
    vehicleId: string,
  ): Promise<AxiosResponse<ICFSMasterVehicle[]>> => {
    return http.get(
      `api/${ApiVersion.V1}/cfs/${cfsId}/master-vehicles/${vehicleId}`,
    );
  },
  deleteCfsMasterVehicle: async (
    cfsId: string,
    vehicleId: string,
  ): Promise<AxiosResponse<ICFSMasterVehicle>> => {
    return http.delete(
      `api/${ApiVersion.V1}/cfs/${cfsId}/master-vehicles/${vehicleId}`,
    );
  },
  addCfsMasterName: async (
    cfsId: string,
    nameId: string,
    data: { relationship: ICFSInvolvement },
  ): Promise<AxiosResponse<ICFSMasterName>> => {
    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/master-names/${nameId}`,
      data,
    );
  },

  updateCfsMasterName: async (
    cfsId: string,
    nameId: string,
    data: { relationship: ICFSInvolvement },
  ): Promise<AxiosResponse<ICFSMasterName>> => {
    return http.put(
      `api/${ApiVersion.V1}/cfs/${cfsId}/master-names/${nameId}`,
      data,
    );
  },
  getCfsMasterNames: async (
    cfsId: string,
  ): Promise<AxiosResponse<ICFSMasterName[]>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${cfsId}/master-names`);
  },
  getCfsMasterName: async (
    cfsId: string,
    nameId: string,
  ): Promise<AxiosResponse<ICFSMasterName>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${cfsId}/master-names/${nameId}`);
  },
  deleteCfsMasterName: async (
    cfsId: string,
    nameId: string,
  ): Promise<AxiosResponse<ICFSMasterName>> => {
    return http.delete(
      `api/${ApiVersion.V1}/cfs/${cfsId}/master-names/${nameId}`,
    );
  },
  saveNarrative: async (
    cfsId: string,
    data: INarrativeRequest,
  ): Promise<AxiosResponse<INarrative>> => {
    return data._id
      ? CfsApi.updateNarrative(cfsId, data._id, data)
      : CfsApi.createNarrative(cfsId, data);
  },
  createNarrative: async (
    cfsId: string,
    data: INarrativeRequest,
  ): Promise<AxiosResponse<INarrative>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/${cfsId}/narratives`, data);
  },
  updateNarrative: async (
    cfsId: string,
    narrativeId: string,
    data: INarrativeRequest,
  ): Promise<AxiosResponse<INarrative>> => {
    return http.put(
      `api/${ApiVersion.V1}/cfs/${cfsId}/narratives/${narrativeId}`,
      data,
    );
  },
  getLinkedNarratives: async (
    cfsId: string,
    params: IGetParams = { sort: JSON.stringify({ createdAt: 1 }) },
  ): Promise<AxiosResponse<INarrative[]>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${cfsId}/narratives`, {
      params,
    });
  },
  deleteNarrative: async (
    cfsId: string,
    narrativeId: string,
  ): Promise<AxiosResponse<ICFSLog>> => {
    return http.delete(
      `api/${ApiVersion.V1}/cfs/${cfsId}/narratives/${narrativeId}`,
    );
  },
  getCfsNarrative: async (
    cfsId: string,
  ): Promise<AxiosResponse<INarrative>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${cfsId}/userNarratives`);
  },
  createMasterNameNote: (
    cfsId: string,
    nameId: string,
    description: string,
  ): Promise<AxiosResponse<INote>> => {
    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/master-names/${nameId}/notes`,
      {
        description,
      },
    );
  },
  getCfsHistory: async (
    cfsId: string,
    params: IGetParams,
  ): Promise<AxiosResponse<IListResponse<ICFSLog>>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${cfsId}/logs`, { params });
  },
  updateTimeline: async (
    cfsId: string,
    data: {
      logs: Array<{
        _id?: string;
        event: string;
        overriddenAt?: number | null;
        groupDay?: number | null;
        sortOrder: number;
        deleted?: boolean;
        data?: {
          new?: {
            description?: string;
          };
        };
      }>;
    },
    params?: { eventType?: string },
  ): Promise<AxiosResponse<ICFSLog[]>> => {
    return http.patch(`api/${ApiVersion.V1}/cfs/${cfsId}/timeline`, data, {
      params,
    });
  },
  getAllCfsHistory: async (
    params: IGetParams,
    filterQuery?: string,
  ): Promise<AxiosResponse<IListResponse<ICFSTimelineLog>>> => {
    if (!filterQuery) {
      return http.get(`api/${ApiVersion.V1}/cfs/logs/all`, { params });
    }
    return http.get(`api/${ApiVersion.V1}/cfs/logs/all${filterQuery}`, {
      params,
    });
  },
  // Set isReadyToDispatch to true in cfs
  setCFSRTD: async (cfsId: string): Promise<AxiosResponse<ICFS>> => {
    return http.patch(`api/${ApiVersion.V1}/cfs/${cfsId}/ready-to-dispatch`);
  },
  refreshUnitShiftDistances: async (
    cfsId: string,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.patch(
      `api/${ApiVersion.V1}/cfs/${cfsId}/unit-shift-distances`,
      {},
    );
  },
  sendToExternalService: async (
    cfsId: string,
  ): Promise<AxiosResponse<IListResponse<ICFSLog>>> => {
    return http.patch(
      `api/${ApiVersion.V1}/cfs/${cfsId}/send-to-external-service`,
    );
  },
  makeFireUnderControl: async (cfsId: string): Promise<AxiosResponse<ICFS>> => {
    return http.patch(
      `api/${ApiVersion.V1}/cfs/${cfsId}/make-fire-under-control`,
    );
  },
  listByUser: async (
    userId: string,
  ): Promise<AxiosResponse<{ assigned: ICFS[]; queued: ICFS[] }>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/user/${userId}`);
  },
  requestBackup: async (
    cfsId: string,
    unitShiftId: string,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/${cfsId}/backup-request`, {
      unitShiftId,
    });
  },
  responseBackup: async (
    cfsId: string,
    accepted: boolean,
  ): Promise<AxiosResponse<{ assigned: ICFS[]; queued: ICFS[] }>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/${cfsId}/backup-response`, {
      accepted,
    });
  },
  assignMe: async (
    cfsId: string,
    isDispatchedToHQ = false,
    headquarterName?: string | null,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/${cfsId}/assign-me`, {
      isDispatchedToHQ,
      headquarterName,
    });
  },
  queueAssignedAndAssignNew: async (
    cfsId: string,
    assignedCfsId: string,
    assignedUnitShiftId: string,
  ): Promise<AxiosResponse<ICFS>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/queue-assigned`, {
      cfsId,
      assignedCfsId,
      assignedUnitShiftId,
    });
  },
  generateAISummary: async (
    id: string,
  ): Promise<AxiosResponse<{ shortDescription: string }>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${id}/ai-summary`);
  },
  getCfsCallerLocations: async (
    cfsId: string,
  ): Promise<AxiosResponse<ICallerLocation[]>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${cfsId}/caller-locations`);
  },
  editAdditionalInfo: async (
    cfsId: string,
    data: { description: string; newDescription: string },
  ): Promise<AxiosResponse> => {
    return http.patch(
      `api/${ApiVersion.V1}/cfs/${cfsId}/update-additional-info`,
      data,
    );
  },
  getAdditionalData: async (id: string): Promise<AxiosResponse<any>> => {
    return http.get(`api/${ApiVersion.V1}/cfs/${id}/caller-additional-data`);
  },
  resendReadyToDispatchNotification: async (
    cfsId: string,
  ): Promise<AxiosResponse<ICallerLocation[]>> => {
    return http.post(`api/${ApiVersion.V1}/cfs/notifications/${cfsId}`);
  },
  uploadFiles: async (
    id: string,
    data: { files: IFile[] },
  ): Promise<AxiosResponse<IFile[]>> => {
    return http.put(`api/${ApiVersion.V1}/cfs/${id}/files`, data);
  },
  createMasterVehicleNote: (
    cfsId: string,
    vehicleId: string,
    description: string,
  ): Promise<AxiosResponse<INote>> => {
    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/master-vehicles/${vehicleId}/notes`,
      {
        description,
      },
    );
  },
  createMasterVehicleKaseId: (
    kaseId: string,
    vehicleId: string,
    description: string,
  ): Promise<AxiosResponse<INote>> => {
    return http.post(
      `api/${ApiVersion.V1}/kases/${kaseId}/master-vehicles/${vehicleId}/notes`,
      {
        description,
      },
    );
  },
  emailCfsFiles: (cfsId: string, data: any): Promise<AxiosResponse> => {
    return http.post(`api/${ApiVersion.V1}/cfs/${cfsId}/files/email`, data);
  },
};
